'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  BracketsIcon,
  CalendarIcon,
  CoinsIcon,
  EditIcon,
  EyeIcon,
  ListOrderedIcon,
  MoreHorizontalIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  TextIcon,
  Trash2Icon,
  TrendingUpIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { SortEnum, SortOrderEnum } from '@/enums'
import { useDeleteCurrency, useCurrencies, useBulkDeleteCurrencies, useSortCurrency } from '@/hooks'
import { useCurrenciesStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { CurrenciesFilterParams } from '@/types/currency'
import {
  DeleteConfirmationDialog,
  LoadingState,
  CurrenciesFilters,
  SortableHeader,
  TableSortActions,
  ExchangeRatesSheet
} from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { formatDateTime } from '@/lib/utils'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { BulkDeleteResponse } from '@/types/general'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteCurrencyId, setDeleteCurrencyId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)
  const [exchangeRatesOpen, setExchangeRatesOpen] = useState<boolean>(false)

  const { mutate: deleteCurrencyMutate, isPending: isDeleting } = useDeleteCurrency()
  const { mutate: bulkDeleteCurrenciesMutate, isPending: isBulkDeleting } = useBulkDeleteCurrencies()
  const { mutate: sortCurrencyMutate, isPending: isSorting } = useSortCurrency()

  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'CURRENCIES', 'CREATE')
  const canShow = hasPermission(currentUser, 'CURRENCIES', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'CURRENCIES', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'CURRENCIES', 'DELETE')

  const selectedCurrencyIds = useCurrenciesStore(state => state.selectedCurrencyIds)
  const toggleCurrencySelection = useCurrenciesStore(state => state.toggleCurrencySelection)
  const selectAllCurrencies = useCurrenciesStore(state => state.selectAllCurrencies)
  const deselectAllCurrencies = useCurrenciesStore(state => state.deselectAllCurrencies)

  const filters = useMemo<CurrenciesFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'order',
      q: params.get('q') || '',
      active: params.get('active') === 'true' ? true : params.get('active') === 'false' ? false : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: currenciesData, isLoading, error, refetch, isFetching } = useCurrencies(filters)

  const updateUrlParams = (newFilters: Partial<CurrenciesFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<CurrenciesFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/currencies')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handleSortOrder = (id: string, type: SortOrderEnum) => {
    sortCurrencyMutate(
      { currencyId: id, type },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['currencies'] })
          toast.success(t('sorted_successfully'))
        },
        onError: (error: any) => {
          console.error('Error sorting currency:', error)
          toast.error(t('failed_to_sort'))
        }
      }
    )
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (currencyId: string) => {
    router.push(`/currencies/${currencyId}`)
  }

  const handleEdit = (currencyId: string) => {
    router.push(`/currencies/${currencyId}/edit`)
  }

  const handleDelete = (currencyId: string) => {
    setDeleteCurrencyId(currencyId)
  }

  const renderActionItems = (currencyId: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        {canShow && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleView(currencyId)
            }}>
            <EyeIcon className="h-4 w-4" />
            {t('currency_details')}
          </ItemComponent>
        )}

        {canUpdate && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleEdit(currencyId)
            }}>
            <EditIcon className="h-4 w-4" />
            {t('edit_currency')}
          </ItemComponent>
        )}
        {canDelete && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleDelete(currencyId)
            }}
            className="text-destructive focus:text-destructive">
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  const confirmDeleteMessage = () => {
    if (deleteCurrencyId) {
      deleteCurrencyMutate(deleteCurrencyId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['currencies'] })
          toast.success(t('deleted_successfully'))
          setDeleteCurrencyId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting currency:', error)
          if (!error.response?.data?.message) {
            toast.error(t('error_deleting'))
          }
          setDeleteCurrencyId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedCurrencyIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteCurrencies = () => {
    if (selectedCurrencyIds.length > 0) {
      bulkDeleteCurrenciesMutate(selectedCurrencyIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['currencies'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (currenciesData && currenciesData.items.length > 0) {
      if (selectedCurrencyIds.length === currenciesData.items.length) {
        deselectAllCurrencies()
      } else {
        selectAllCurrencies(currenciesData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CoinsIcon className="h-5 w-5" />
                {t('currencies')}
              </CardTitle>
              <CardDescription>{t('currencies_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline" onClick={() => setExchangeRatesOpen(true)}>
                <TrendingUpIcon className="h-4 w-4" />
                <span className="hidden items-center gap-2 lg:flex">{t('exchange_rates')}</span>
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/currencies/create">
                    <SquarePlusIcon />
                    <span className="hidden items-center gap-2 lg:flex">{t('create_currency')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedCurrencyIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedCurrencyIds.length})</span>
                  </div>
                </Button>
              )}
              <CurrenciesFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {currenciesData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            {canDelete && (
                              <Checkbox
                                checked={
                                  currenciesData &&
                                  currenciesData.items.length > 0 &&
                                  selectedCurrencyIds.length === currenciesData.items.length
                                }
                                onCheckedChange={() => {
                                  handleSelectAll()
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-30 text-center">
                          <SortableHeader
                            sortBy="code"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <BracketsIcon className="h-4 w-4" />
                            {t('code')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="name"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <TextIcon className="h-4 w-4" />
                            {t('name')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <CoinsIcon className="h-4 w-4" />
                            {t('symbol')}
                          </div>
                        </TableHead>
                        <TableHead className="w-20 text-center">
                          <SortableHeader
                            sortBy="order"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <ListOrderedIcon className="h-4 w-4" />
                            {t('sort_order')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-25 text-center">
                          <div className="flex items-center justify-center gap-2">{t('is_default')}</div>
                        </TableHead>
                        <TableHead className="min-w-25 text-center">
                          <SortableHeader
                            sortBy="active"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CoinsIcon className="h-4 w-4" />
                            {t('active')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currenciesData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        currenciesData.items.map((currency, index) => (
                          <ContextMenu key={currency.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(currency.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={selectedCurrencyIds.includes(currency.id)}
                                        onCheckedChange={() => toggleCurrencySelection(currency.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-mono">{currency.code}</TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>{currency.name}</span>
                                    <span className="text-muted-foreground text-xs">ID: {currency.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-mono">{currency.symbol}</TableCell>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <span className="text-muted-foreground min-w-6 text-right text-sm tabular-nums">
                                      {currency.order}
                                    </span>
                                    {canUpdate && (
                                      <TableSortActions
                                        onSortUp={() => handleSortOrder(currency.id, SortOrderEnum.UP)}
                                        onSortDown={() => handleSortOrder(currency.id, SortOrderEnum.DOWN)}
                                        disabled={isSorting}
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={currency.isDefault ? 'success' : 'outline'}>
                                    {currency.isDefault ? t('yes') : t('no')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={currency.active ? 'success' : 'secondary'}>
                                    {currency.active ? t('active') : t('inactive')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(currency.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(currency.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(currency.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(currency.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={currenciesData.page}
                  totalPages={currenciesData.pages}
                  totalItems={currenciesData.total}
                  pageSize={currenciesData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={deleteCurrencyId !== null}
          onCancel={() => setDeleteCurrencyId(null)}
          onConfirm={confirmDeleteMessage}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteCurrencies}
          isDeleting={isBulkDeleting}
        />
      )}

      <ExchangeRatesSheet open={exchangeRatesOpen} onOpenChange={setExchangeRatesOpen} />
    </>
  )
}

export default List
